import React, {useEffect, useState} from 'react';
import DataTable from 'react-data-table-component';
import TableSkeleton from '../../utils/TableSkeleton';
import {customStyles} from "../../utils/DataTableCustomStyle";
import * as Constants from "../../utils/Constants";
import {generateRandomNumber, getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast} from '../../utils/utility';
import {
    buildPaginationParams,
    DEFAULT_PAGE_SIZE,
    extractPaginationHeadersFromFetch,
    PAGE_SIZE_OPTIONS,
} from '../../utils/paginationUtils';
import TaskDetailsPanel from './TaskDetailsPanel';
import {Tooltip} from 'react-tooltip';
import {hasPermission, ROLES, getRole} from '../../utils/rolePermissions';
import "../../styles/components/tasks.css";

const TaskList = ({ onCopyTask }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [statusFilter, setStatusFilter] = useState('All');

    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(1);

    // Employee sees only their own assigned tasks; managers see all
    const canManageTasks = hasPermission("tasks_manage");
    const isEmployee     = getRole() === ROLES.CLIENT_EMPLOYEE;

    useEffect(() => {
        fetchTasks(1, perPage);
    }, []);

    useEffect(() => {
        if (statusFilter === 'All') setFilteredTasks(tasks);
        else setFilteredTasks(tasks.filter(task => task.taskStatus === statusFilter));
    }, [tasks, statusFilter]);

    const fetchTasks = (page, size) => {
        setLoading(true);
        const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/tasks`;
        const params   = buildPaginationParams(page, size);
        // Scope to the employee's own assignments when role is CLIENT_EMPLOYEE
        const userParam = isEmployee ? `&userId=${getLoggedInUserId()}` : '';
        const queryString = `page=${params.page}&size=${params.size}${userParam}`;

        fetch(`${Constants.BASE_URL}${endPoint}?${queryString}`, {
            method: 'GET',
            headers: { 'Authorization': `${Constants.BEARER} ${getAuthToken()}` }
        })
            .then(response => {
                const pagination = extractPaginationHeadersFromFetch(response);
                return response.json().then(data => ({ data, pagination }));
            })
            .then(({ data, pagination }) => {
                const taskData = data.responseData || [];
                setTasks(taskData);
                setSelectedTasks([]);
                setTotalRows(pagination ? pagination.totalCount : taskData.length);
                const filterEl = document.getElementById("status-filter");
                if (filterEl) filterEl.value = "All";
                setStatusFilter('All');
            })
            .catch(error => console.error('Error fetching tasks:', error))
            .finally(() => setLoading(false));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchTasks(page, perPage);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchTasks(page, newPerPage);
    };

    const editTask = (taskIds, status) => {
        const requestBody = {
            requestBody: { taskIds, taskStatus: status },
            requestId: generateRandomNumber().toString(),
            clientId: getClientIdOfUser(),
            userId: getLoggedInUserId(),
        };
        fetch(`${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/tasks/updateTaskList`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify(requestBody)
        })
            .then(response => response.json())
            .then(data => {
                showToast(data.responseData, { type: 'success' });
                setSelectedTasks([]);
                fetchTasks(currentPage, perPage);
            })
            .catch(error => console.error('Error updating task status:', error));
    };

    const handleRowSelected = (state) => {
        setSelectedTasks(state.selectedRows.map(row => row.taskId));
    };

    const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);

    const columns = [
        { name: 'Task ID',   selector: (row) => row.taskId,   sortable: true, width: '90px' },
        { name: 'Device ID', selector: (row) => row.deviceId, sortable: true, width: '130px' },
        { name: 'Title',     selector: (row) => row.title,    sortable: true, grow: 1.5, wrap: true },
        {
            name: 'Status',
            selector: (row) => row.taskStatus,
            sortable: true,
            width: '130px',
            cell: row => (
                <span style={{
                    fontWeight: 'bold', padding: '4px 8px', borderRadius: '4px',
                    background: row.taskStatus === 'COMPLETED' ? '#dcfce7' : row.taskStatus === 'CANCELLED' ? '#fee2e2' : '#e0f2fe',
                    color:      row.taskStatus === 'COMPLETED' ? '#166534' : row.taskStatus === 'CANCELLED' ? '#991b1b' : '#0369a1'
                }}>{row.taskStatus}</span>
            )
        },
        {
            name: 'Start Time',
            selector: (row) => row.startTime,
            sortable: true,
            width: '160px',
            cell: row => row.startTime ? new Date(row.startTime).toLocaleString('en-IN', { timeZone: 'UTC' }) : 'N/A'
        },
        {
            name: 'End Time',
            selector: (row) => row.endTime,
            sortable: true,
            width: '160px',
            cell: row => row.endTime ? new Date(row.endTime).toLocaleString('en-IN', { timeZone: 'UTC' }) : 'N/A'
        },
        { name: 'Geo fence(s) Configured', selector: (row) => row.personalized, sortable: true, width: '120px', wrap: true },
        { name: 'Entity', selector: (row) => row.assignedEntity || 'None', sortable: true, width: '130px', wrap: true },
        // Copy (Actions) column: Admin & Manager only
        ...(canManageTasks ? [{
            name: 'Actions',
            cell: (row) => (
                <button
                    className="yekkada-btn secondary"
                    style={{ padding: '5px 10px', fontSize: '11px' }}
                    onClick={() => onCopyTask(row)}
                >
                    Copy
                </button>
            ),
            button: true,
            width: '80px'
        }] : []),
    ];

    return (
        <div className="h-full flex flex-col gap-3">

            {/* Context banner — moved to a clean info-tone card */}
            <div className="shrink-0 rounded-lg bg-info-50 border border-info-100 border-l-4 border-l-info-500 px-4 py-3 text-sm text-info-900">
                <strong>About Tasks:</strong> A Task is a specific work assignment for an individual GPS lock device,
                with a defined start/end time and optional geofence configuration.
                Tasks are <em>different from Fleet Trips</em> — a Fleet Trip groups multiple devices for monitoring
                and alert purposes, while a Task tracks a single device's operational activity.
                {canManageTasks && (
                    <> Select one or more tasks using the checkboxes, then use <strong>Start</strong>, <strong>Complete</strong>, or <strong>Cancel</strong> to update their status.</>
                )}
            </div>

            {/* Filter + bulk-action bar */}
            <div className="shrink-0 flex flex-wrap items-center gap-3 bg-white border border-ink-200 rounded-xl shadow-xs px-4 py-3">
                <label className="text-sm font-medium text-ink-700 mr-1" htmlFor="status-filter">Filter status</label>
                <select
                    id="status-filter"
                    onChange={handleStatusFilterChange}
                    className="h-9 px-3 pr-8 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
                >
                    <option value="All">All tasks</option>
                    <option value="ASSIGNED">Assigned</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                </select>

                {canManageTasks && (
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-ink-500">
                            {selectedTasks.length > 0 ? `${selectedTasks.length} selected` : "Select tasks to act"}
                        </span>
                        <button
                            className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md bg-success-600 text-white hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => editTask(selectedTasks, 'IN_PROGRESS')}
                            disabled={selectedTasks.length === 0}
                        >
                            Start
                        </button>
                        <button
                            className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => editTask(selectedTasks, 'COMPLETED')}
                            disabled={selectedTasks.length === 0}
                        >
                            Complete
                        </button>
                        <button
                            className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md bg-white border border-danger-300 text-danger-700 hover:bg-danger-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => editTask(selectedTasks, 'CANCELLED')}
                            disabled={selectedTasks.length === 0}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xs">
                <DataTable
                    columns={columns}
                    data={filteredTasks}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={loading}
                    progressComponent={<TableSkeleton />}
                    expandableRows
                    selectableRows={canManageTasks}
                    selectableRowsHighlight={canManageTasks}
                    onSelectedRowsChange={handleRowSelected}
                    expandableRowsComponent={({ data }) => <TaskDetailsPanel task={data} />}
                    customStyles={customStyles}
                />
            </div>
            <Tooltip id="my-tooltip" />
        </div>
    );
};

export default TaskList;
